<template>
  <div class="model-viewer">
    <div ref="viewerContainer" class="viewer-container"></div>
    
    <div v-if="loading" class="viewer-loading">
      <div class="spinner"></div>
      <p>Loading 3D model...</p>
    </div>
    
    <div v-if="error" class="viewer-error">
      <p>{{ error }}</p>
      <button @click="retryLoad">Retry</button>
    </div>
    
    <div v-if="modelLoaded && !viewOnly" class="base-controls-stack">
      <div class="base-shape-toggle">
        <button :class="{ active: !showSizeRef }" @click="showSizeRef = false; updateSizeRefCube()" title="No size reference">⊘</button>
        <button :class="{ active: showSizeRef && sizeRefScale === 1 }" @click="setSizeRef(1)" title="Human size reference">🧍 Hum</button>
        <button :class="{ active: showSizeRef && sizeRefScale === 3.5 }" @click="setSizeRef(3.5)" title="Ogre size reference">👹 Ogr</button>
        <button :class="{ active: showSizeRef && sizeRefScale === 8 }" @click="setSizeRef(8)" title="Giant size reference">🗿 Gnt</button>
      </div>
      <div class="base-shape-toggle">
        <button :class="{ active: baseShape === 'none' }" @click="setBaseShape('none')" title="No Base">⊘</button>
        <button :class="{ active: baseShape === 'square' }" @click="setBaseShape('square')" title="Square Base">⬛</button>
        <button :class="{ active: baseShape === 'round' }" @click="setBaseShape('round')" title="Round Base">⬤</button>
        <button v-if="baseShape !== 'none'" :class="{ active: baseEditMode }" @click="toggleBaseEditMode" title="Base Size">📐</button>
      </div>
    </div>

    <div class="ctrl-group ctrl-group--left" :class="{ 'ctrl-group--collapsed': cameraGroupCollapsed }">
      <span class="ctrl-group-icon" @click="cameraGroupCollapsed = !cameraGroupCollapsed">📷</span>
      <button v-show="!cameraGroupCollapsed" @click="bottomView" title="Bottom View">👇 Bot</button>
      <button v-show="!cameraGroupCollapsed" @click="topView" title="Top View">👆 Top</button>
      <button v-show="!cameraGroupCollapsed" @click="resetCamera" title="Reset View">📷 View</button>
      <button v-show="!cameraGroupCollapsed" @click="toggleAutoRotate" :class="{ active: autoRotate }" title="Auto Rotate">🔄 Auto</button>
    </div>

    <div v-if="!viewOnly" class="ctrl-group ctrl-group--right" :class="{ 'ctrl-group--collapsed': toolGroupCollapsed }">
      <span class="ctrl-group-icon" @click="toolGroupCollapsed = !toolGroupCollapsed">🔧</span>
      <button v-show="!toolGroupCollapsed" v-if="modelLoaded" @click="toggleCutMode" :class="{ active: cutMode }" title="Cut Tool">✂ Cut</button>
      <button v-show="!toolGroupCollapsed" v-if="modelLoaded" @click="toggleSizeEditMode" :class="{ active: sizeEditMode }" title="Model Scale">↕ Size</button>
      <button v-show="!toolGroupCollapsed" v-if="modelLoaded" @click="togglePivotMode" :class="{ active: pivotMode }" title="Reposition Model">↔ Move</button>
      <button v-show="!toolGroupCollapsed" v-if="modelLoaded" @click="toggleRotateMode" :class="{ active: rotateMode }" title="Rotate Model">🔁 Rotate</button>
      <button v-show="!toolGroupCollapsed" v-if="actionHistory.length > 0" @click="undoAction" title="Undo" class="undo-cut-btn">↩</button>
    </div>

    <div
      v-if="cutMode && !viewOnly"
      class="cut-drag-overlay"
    ></div>

    <div v-if="cutMode && !viewOnly" class="cut-panel tool-panel">
      <span class="tool-panel-label">✂ Cut</span>
      <div class="tool-panel-content">
        <button class="apply-cut-btn" @click="applyCut" title="Apply Cut">✔</button>
      </div>
      <button class="panel-close" @click="toggleCutMode">✕</button>
    </div>

    <div v-if="baseEditMode && !viewOnly" class="tool-panel tool-panel--vertical">
      <div class="tool-panel-header">
        <span class="tool-panel-label">📐 Base Size</span>
        <button class="panel-close" @click="toggleBaseEditMode">✕</button>
      </div>
      <div class="tool-panel-content">
        <label class="base-size-field">
          W
          <input type="number" v-model.number="baseWidth" min="1" max="200" step="1" @change="updateReferenceCube" @focus="$event.target.select()" />
          <span class="base-size-unit">mm</span>
        </label>
        <label class="base-size-field">
          L
          <input type="number" v-model.number="baseLength" min="1" max="200" step="1" @change="updateReferenceCube" @focus="$event.target.select()" />
          <span class="base-size-unit">mm</span>
        </label>
      </div>
    </div>

    <div v-if="sizeEditMode && !viewOnly" class="tool-panel">
      <span class="tool-panel-label">↕ Scale</span>
      <div class="tool-panel-content">
        <span class="pivot-hint">Drag up/down to scale</span>
      </div>
      <button class="panel-close" @click="toggleSizeEditMode">✕</button>
    </div>

    <div v-if="pivotMode && !viewOnly" class="tool-panel">
      <span class="tool-panel-label">↔ Move</span>
      <div class="tool-panel-content">
        <span class="pivot-hint">Left-drag to reposition</span>
      </div>
      <button class="panel-close" @click="togglePivotMode">✕</button>
    </div>

    <div v-if="rotateMode && !viewOnly" class="tool-panel">
      <span class="tool-panel-label">🔁 Rotate</span>
      <div class="tool-panel-content">
        <span class="pivot-hint">Drag left/right to rotate</span>
      </div>
      <button class="panel-close" @click="toggleRotateMode">✕</button>
    </div>

    <!-- Base Setup Modal -->
    <div v-if="showBaseSetupModal && !viewOnly" class="base-setup-overlay">
      <div class="base-setup-modal">

        <p class="base-setup-section-label">Reference size:</p>
        <div class="base-setup-shapes">
          <button
            class="base-setup-shape-btn"
            :class="{ active: setupSizeScale === null }"
            @click="setupSizeScale = null"
          >⊘ None</button>
          <button
            class="base-setup-shape-btn"
            :class="{ active: setupSizeScale === 1 }"
            @click="setupSizeScale = 1"
          >🧍 Human</button>
          <button
            class="base-setup-shape-btn"
            :class="{ active: setupSizeScale === 3.5 }"
            @click="setupSizeScale = 3.5"
          >👹 Ogre</button>
          <button
            class="base-setup-shape-btn"
            :class="{ active: setupSizeScale === 8 }"
            @click="setupSizeScale = 8"
          >🗿 Giant</button>
        </div>

        <p class="base-setup-section-label">Reference base:</p>
        <div class="base-setup-shapes">
          <button
            class="base-setup-shape-btn"
            :class="{ active: setupShape === 'none' }"
            @click="changeSetupShape('none')"
          >⊘ No Base</button>
          <button
            class="base-setup-shape-btn"
            :class="{ active: setupShape === 'square' }"
            @click="changeSetupShape('square')"
          >⬛ Square</button>
          <button
            class="base-setup-shape-btn"
            :class="{ active: setupShape === 'round' }"
            @click="changeSetupShape('round')"
          >⬤ Round</button>
        </div>

        <div v-if="setupShape !== 'none'" class="base-setup-size-row">
          <span class="base-setup-field-label">Size</span>
          <select class="base-setup-select" v-model="setupPreset" @change="selectPreset(setupPreset)">
            <option :value="20">20 mm</option>
            <option :value="25">25 mm</option>
            <option :value="30">30 mm</option>
            <option :value="32">32 mm</option>
            <option :value="40">40 mm</option>
            <option :value="50">50 mm</option>
            <option value="custom">Custom</option>
          </select>
        </div>

        <div v-if="setupShape !== 'none' && setupPreset === 'custom'" class="base-setup-custom-fields" style="margin-bottom:14px;">
          <div class="base-setup-field">
            <span class="base-setup-field-label">Width</span>
            <input type="number" v-model.number="setupWidth" min="1" max="200" />
            <span class="base-setup-unit">mm</span>
          </div>
          <div class="base-setup-field">
            <span class="base-setup-field-label">Length</span>
            <input type="number" v-model.number="setupLength" min="1" max="200" />
            <span class="base-setup-unit">mm</span>
          </div>
        </div>

        <div class="base-setup-actions">
          <button class="base-setup-confirm" @click="confirmBaseSetup">Confirm</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils';

// Calibration factor applied to all exported/linked model scales.
// Adjust this single value to change the in-game size of all models.
const MODEL_SCALE_FACTOR = 3;

export default {
  name: 'ModelViewer',
  props: {
    modelUrl: {
      type: String,
      required: true
    },
    modelData: {
      type: Object,
      default: () => ({})
    },
    viewOnly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      scene: null,
      camera: null,
      renderer: null,
      controls: null,
      model: null,
      loading: true,
      error: null,
      modelLoaded: false,
      autoRotate: false,
      animationFrameId: null,
      baseBBox: null,
      baseBBoxHelper: null,
      cutMode: false,
      cutPercent: 10,
      modelHeight: 0,
      cutPlaneHelper: null,
      cutHistory: [],
      actionHistory: [],
      baseShape: 'square',
      baseWidth: 30,
      baseLength: 30,
      baseEditMode: false,
      referenceCube: null,
      modelScale: 1.0,
      sizeEditMode: false,
      sizeEditMin: 0.1,
      sizeEditMax: 3,
      pivotMode: false,
      rotateMode: false,
      rotateYAngle: 0,
      perspCamera: null,
      orthoCamera: null,
      originalMaxDim: 1,
      cameraGroupCollapsed: false,
      toolGroupCollapsed: false,
      showSizeRef: true,
      sizeRefCube: null,
      showBaseSetupModal: false,
      setupShape: 'square',
      setupPreset: 25,
      setupWidth: 25,
      setupLength: 25,
      setupSizeScale: null,
      sizeRefScale: 1,
      frontArrow: null,
      sourceFormat: null,  // 'glb' | 'fbx' | 'obj' — set when model loads, used by exportGLB
      // Accumulated transforms for Link meshes (baked progressively by closeAllPanels)
      accumulatedRotationY: 0,
      accumulatedOffsetX: 0,
      accumulatedOffsetZ: 0,
    };
  },
  mounted() {
    if (this.viewOnly) this.baseShape = 'none';
    try {
      const saved = localStorage.getItem('warhall_base_setup');
      if (saved) {
        const prefs = JSON.parse(saved);
        if (prefs.setupShape !== undefined) this.setupShape = prefs.setupShape;
        if (prefs.setupPreset !== undefined) this.setupPreset = prefs.setupPreset;
        if (prefs.setupWidth !== undefined) this.setupWidth = prefs.setupWidth;
        if (prefs.setupLength !== undefined) this.setupLength = prefs.setupLength;
        if ('setupSizeScale' in prefs) this.setupSizeScale = prefs.setupSizeScale;
      }
    } catch (e) { /* storage unavailable */ }
    this.initThreeJS();
    this.loadModel();
    window.addEventListener('resize', this.onWindowResize);
  },
  beforeDestroy() {
    // Clean up
    window.removeEventListener('resize', this.onWindowResize);
    
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    
    if (this.controls) {
      this.controls.dispose();
    }
    
    if (this.renderer) {
      this.renderer.dispose();
    }

    this.hideCutPlane();
    this.removeReferenceCube();
    this.removeFrontArrow();
    this.removeSizeRefCube();
    if (this._pivotMouseDownHandler && this.renderer) {
      this.renderer.domElement.removeEventListener('mousedown', this._pivotMouseDownHandler);
    }
    if (this._pivotTouchStartHandler && this.renderer) {
      this.renderer.domElement.removeEventListener('touchstart', this._pivotTouchStartHandler);
    }
    if (this._cutMouseDownHandler && this.renderer) {
      this.renderer.domElement.removeEventListener('mousedown', this._cutMouseDownHandler);
    }
    if (this._cutTouchStartHandler && this.renderer) {
      this.renderer.domElement.removeEventListener('touchstart', this._cutTouchStartHandler);
    }
    if (this._rotateMouseDownHandler && this.renderer) {
      this.renderer.domElement.removeEventListener('mousedown', this._rotateMouseDownHandler);
    }
    if (this._rotateTouchStartHandler && this.renderer) {
      this.renderer.domElement.removeEventListener('touchstart', this._rotateTouchStartHandler);
    }

    // Dispose geometry and materials
    if (this.model) {
      this.model.traverse((child) => {
        if (child.geometry) {
          child.geometry.dispose();
        }
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(material => material.dispose());
          } else {
            child.material.dispose();
          }
        }
      });
    }
  },
  computed: {
  },
  watch: {
    modelScale(v) {
      if (this.model) {
        this.model.scale.set(v, v, v);
        this.updateReferenceCube();
      }
    },
    rotateYAngle(deg) {
      if (this.model && this.rotateMode) {
        this.model.rotation.y = THREE.MathUtils.degToRad(deg);
      }
    }
  },
  methods: {
    initThreeJS() {
      // Scene
      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(0x2c3e50);
      
      // Camera
      this.camera = new THREE.PerspectiveCamera(
        75,
        this.$refs.viewerContainer.clientWidth / this.$refs.viewerContainer.clientHeight,
        0.1,
        1000
      );
      this.camera.position.set(0, 1, 3);
      this.perspCamera = this.camera;
      
      // Renderer
      this.renderer = new THREE.WebGLRenderer({ antialias: true });
      this.renderer.setSize(
        this.$refs.viewerContainer.clientWidth,
        this.$refs.viewerContainer.clientHeight
      );
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      this.$refs.viewerContainer.appendChild(this.renderer.domElement);
      
      // Lights - Enhanced lighting setup
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);
      this.scene.add(ambientLight);
      
      // Hemisphere light for natural ambient
      const hemiLight = new THREE.HemisphereLight(0xffffff, 0x444444, 0.6);
      hemiLight.position.set(0, 20, 0);
      this.scene.add(hemiLight);
      
      // Main directional light (key light)
      const directionalLight1 = new THREE.DirectionalLight(0xffffff, 1.2);
      directionalLight1.position.set(5, 8, 5);
      directionalLight1.castShadow = true;
      this.scene.add(directionalLight1);
      
      // Fill light (from opposite side)
      const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.6);
      directionalLight2.position.set(-5, 4, -5);
      this.scene.add(directionalLight2);
      
      // Back light (rim light)
      const directionalLight3 = new THREE.DirectionalLight(0xffffff, 0.5);
      directionalLight3.position.set(0, 5, -8);
      this.scene.add(directionalLight3);
      
      // Controls
      this.controls = new OrbitControls(this.camera, this.renderer.domElement);
      this.controls.enableDamping = true;
      this.controls.dampingFactor = 0.05;
      this.controls.minDistance = 0.5;
      this.controls.maxDistance = 50;
      this.controls.enablePan = false;
      this.controls.enableZoom = true;
      // Left click = pan, right click = rotate
      this.controls.mouseButtons = {
        LEFT: THREE.MOUSE.PAN,
        MIDDLE: THREE.MOUSE.DOLLY,
        RIGHT: THREE.MOUSE.ROTATE
      };
      // Touch: single finger = tools only (camera disabled), two fingers = rotate + pinch-zoom
      this.controls.touches = {
        ONE: -1,
        TWO: THREE.TOUCH.DOLLY_ROTATE
      };
      
      // Start animation loop
      this.animate();
    },
    
    async loadModel() {
      this.loading = true;
      this.error = null;
      
      try {
        let objectUrl;
        let shouldRevoke = false;
        let sniffedExt = null;

        if (this.modelUrl.startsWith('blob:')) {
          // Local file — blob URLs can be used directly without fetching
          objectUrl = this.modelUrl;
        } else {
          // Remote URL — download first
          const isExternal = this.modelUrl.startsWith('http://') || this.modelUrl.startsWith('https://');
          const response = await fetch(this.modelUrl, {
            credentials: isExternal ? 'omit' : 'include'
          });
          
          if (!response.ok) {
            throw new Error('Failed to download model');
          }
          
          const blob = await response.blob();

          // Sniff first 20 bytes to detect format when URL has no clear extension
          try {
            const header = new Uint8Array(await blob.slice(0, 20).arrayBuffer());
            // GLB: magic "glTF" = 0x46546C67
            if (header[0] === 0x67 && header[1] === 0x6C && header[2] === 0x54 && header[3] === 0x46) {
              sniffedExt = 'glb';
            // FBX binary: "Kaydara FBX"
            } else if (header[0] === 0x4B && header[1] === 0x61 && header[2] === 0x79) {
              sniffedExt = 'fbx';
            // GLTF JSON: starts with '{'
            } else if (header[0] === 0x7B) {
              sniffedExt = 'gltf';
            // OBJ: starts with '#', 'v', 'o', 'g', 'f', or 'mtllib'
            } else if ([0x23, 0x76, 0x6F, 0x67, 0x66, 0x6D].includes(header[0])) {
              sniffedExt = 'obj';
            }
          } catch (_) { /* ignore sniff errors, fall through to filename-based detection */ }

          objectUrl = URL.createObjectURL(blob);
          shouldRevoke = true;
        }

        // Determine format: sniffed content > fileName extension > default GLB
        const fileName = (this.modelData && this.modelData.fileName) ? this.modelData.fileName : this.modelUrl;
        const ext = sniffedExt || fileName.split('.').pop().toLowerCase();

        const onLoaded = (object) => {
          this._placeModelInScene(object);
          if (shouldRevoke) URL.revokeObjectURL(objectUrl);
        };
        const onError = (error) => {
          console.error('Model loading error:', error);
          this.error = 'Failed to load 3D model. Please try again.';
          this.loading = false;
          if (shouldRevoke) URL.revokeObjectURL(objectUrl);
        };

        if (ext === 'fbx') {
          this.sourceFormat = 'fbx';
          const loader = new FBXLoader();
          loader.load(objectUrl, onLoaded, undefined, onError);
        } else if (ext === 'obj') {
          this.sourceFormat = 'obj';
          const loader = new OBJLoader();
          loader.load(objectUrl, onLoaded, undefined, onError);
        } else {
          this.sourceFormat = 'glb';
          // Default: GLB / GLTF
          const loader = new GLTFLoader();
          loader.load(objectUrl, (gltf) => onLoaded(gltf.scene), undefined, onError);
        }
      } catch (error) {
        console.error('Model fetch error:', error);
        this.error = error.message || 'Failed to load model';
        this.loading = false;
      }
    },

    _placeModelInScene(object) {
      this.model = object;

      // Center and scale model
      const box = new THREE.Box3().setFromObject(object);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());

      // Center the model on all axes first
      object.position.sub(center);

      // Scale to fit
      const maxDim = Math.max(size.x, size.y, size.z);
      this.originalMaxDim = maxDim;
      const scale = 2 / maxDim;
      object.scale.multiplyScalar(scale);

      // Apply base-pivot: bottom sits at y=0, centered on XZ
      object.updateMatrixWorld(true);
      const scaledBox = new THREE.Box3().setFromObject(object);
      const scaledCenter = scaledBox.getCenter(new THREE.Vector3());
      object.position.x -= scaledCenter.x;
      object.position.z -= scaledCenter.z;
      object.position.y -= scaledBox.min.y;

      // Bake display transform into geometry, then apply ×4 scale and 90° Y rotation
      object.updateMatrixWorld(true);
      object.traverse((child) => {
        if (!child.isMesh) return;
        child.geometry = child.geometry.clone();
        child.geometry.applyMatrix4(child.matrixWorld);
        child.position.set(0, 0, 0);
        child.rotation.set(0, 0, 0);
        child.scale.set(1, 1, 1);
        child.updateMatrix();
      });
      object.position.set(0, 0, 0);
      object.rotation.set(0, 0, 0);
      object.scale.set(1, 1, 1);
      object.updateMatrixWorld(true);

      const scaleMatrix = new THREE.Matrix4().makeScale(4, 4, 4);
      const rotationMatrix = new THREE.Matrix4().makeRotationY(-Math.PI / 2);
      object.traverse((child) => {
        if (!child.isMesh) return;
        child.geometry.applyMatrix4(scaleMatrix);
        child.geometry.applyMatrix4(rotationMatrix);
      });
      object.updateMatrixWorld(true);

      // Recompute bounding box after transform
      const finalBox = new THREE.Box3().setFromObject(object);
      const finalSize = finalBox.getSize(new THREE.Vector3());

      // Point camera and orbit target at the model's vertical midpoint
      this.controls.target.set(0, finalSize.y / 2, 0);
      this.camera.position.set(0, finalSize.y / 2, finalSize.y * 2);
      this.controls.update();

      // Store model height (base is at y=0, top at finalSize.y)
      this.modelHeight = finalSize.y;

      this.scene.add(object);
      this.addReferenceCube();

      // Apply diffuse texture if provided (used for OBJ uploads)
      if (this.modelData && this.modelData.textureUrl) {
        const textureLoader = new THREE.TextureLoader();
        textureLoader.load(this.modelData.textureUrl, (texture) => {
          texture.colorSpace = THREE.SRGBColorSpace;
          object.traverse((child) => {
            if (!child.isMesh) return;
            child.material = new THREE.MeshStandardMaterial({ map: texture });
            child.material.needsUpdate = true;
          });
        });
      }

      this.loading = false;
      this.modelLoaded = true;
      this.cutHistory = [];
      this.actionHistory = [];

      if (!this.viewOnly) {
        this.showBaseSetupModal = true;
      }
    },
    
    animate() {
      this.animationFrameId = requestAnimationFrame(this.animate);
      
      if (this.controls) {
        this.controls.update();
      }
      
      if (this.renderer && this.scene && this.camera) {
        this.renderer.render(this.scene, this.camera);
      }
    },
    
    onWindowResize() {
      if (!this.$refs.viewerContainer) return;
      
      const width = this.$refs.viewerContainer.clientWidth;
      const height = this.$refs.viewerContainer.clientHeight;
      
      this.perspCamera.aspect = width / height;
      this.perspCamera.updateProjectionMatrix();

      if (this.orthoCamera) {
        const aspect = width / height;
        const halfH = this.orthoCamera.top;
        this.orthoCamera.left   = -halfH * aspect;
        this.orthoCamera.right  =  halfH * aspect;
        this.orthoCamera.updateProjectionMatrix();
      }

      this.renderer.setSize(width, height);
    },
    
    resetCamera() {
      // Switch back to perspective if currently in ortho view
      if (this.orthoCamera && this.camera === this.orthoCamera) {
        this.camera = this.perspCamera;
        this.controls.object = this.perspCamera;
      }
      this.camera.up.set(0, 1, 0);
      this.camera.position.set(0, 3, 8);
      this.camera.lookAt(0, 1.5, 0);
      this.controls.target.set(0, 1.5, 0);
      this.controls.update();
    },

    topView() {
      if (!this.model) return;
      const box = new THREE.Box3().setFromObject(this.model);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.z);
      const aspect = this.$refs.viewerContainer.clientWidth / this.$refs.viewerContainer.clientHeight;
      const halfH = maxDim / 2 * 1.3;
      const halfW = halfH * aspect;
      const distance = maxDim * 4;

      if (!this.orthoCamera) {
        this.orthoCamera = new THREE.OrthographicCamera(
          -halfW, halfW, halfH, -halfH, 0.1, 1000
        );
      } else {
        this.orthoCamera.left   = -halfW;
        this.orthoCamera.right  =  halfW;
        this.orthoCamera.top    =  halfH;
        this.orthoCamera.bottom = -halfH;
        this.orthoCamera.updateProjectionMatrix();
      }

      this.orthoCamera.position.set(center.x, center.y + distance, center.z);
      this.orthoCamera.up.set(0, 0, 1);
      this.orthoCamera.lookAt(center.x, center.y, center.z);
      this.orthoCamera.updateProjectionMatrix();

      this.camera = this.orthoCamera;
      this.controls.object = this.orthoCamera;
      this.controls.target.set(center.x, center.y, center.z);
      this.controls.update();
    },

    topView() {
      if (!this.model) return;
      const box = new THREE.Box3().setFromObject(this.model);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.z);
      const aspect = this.$refs.viewerContainer.clientWidth / this.$refs.viewerContainer.clientHeight;
      const halfH = maxDim / 2 * 1.3;
      const halfW = halfH * aspect;
      const distance = maxDim * 4;

      if (!this.orthoCamera) {
        this.orthoCamera = new THREE.OrthographicCamera(
          -halfW, halfW, halfH, -halfH, 0.1, 1000
        );
      } else {
        this.orthoCamera.left   = -halfW;
        this.orthoCamera.right  =  halfW;
        this.orthoCamera.top    =  halfH;
        this.orthoCamera.bottom = -halfH;
        this.orthoCamera.updateProjectionMatrix();
      }

      this.orthoCamera.position.set(center.x, center.y + distance, center.z);
      this.orthoCamera.up.set(0, 0, 1);
      this.orthoCamera.lookAt(center.x, center.y, center.z);
      this.orthoCamera.updateProjectionMatrix();

      this.camera = this.orthoCamera;
      this.controls.object = this.orthoCamera;
      this.controls.target.set(center.x, center.y, center.z);
      this.controls.update();
    },

    bottomView() {
      if (!this.model) return;
      const box = new THREE.Box3().setFromObject(this.model);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.z);
      const aspect = this.$refs.viewerContainer.clientWidth / this.$refs.viewerContainer.clientHeight;
      const halfH = maxDim / 2 * 1.3;
      const halfW = halfH * aspect;
      const distance = maxDim * 4;

      // Create or reuse the orthographic camera
      if (!this.orthoCamera) {
        this.orthoCamera = new THREE.OrthographicCamera(
          -halfW, halfW, halfH, -halfH, 0.1, 1000
        );
      } else {
        this.orthoCamera.left   = -halfW;
        this.orthoCamera.right  =  halfW;
        this.orthoCamera.top    =  halfH;
        this.orthoCamera.bottom = -halfH;
        this.orthoCamera.updateProjectionMatrix();
      }

      this.orthoCamera.position.set(center.x, center.y - distance, center.z);
      this.orthoCamera.up.set(0, 0, -1);
      this.orthoCamera.lookAt(center.x, center.y, center.z);
      this.orthoCamera.updateProjectionMatrix();

      // Swap to ortho camera
      this.camera = this.orthoCamera;
      this.controls.object = this.orthoCamera;
      this.controls.target.set(center.x, center.y, center.z);
      this.controls.update();
    },
    
    toggleAutoRotate() {
      this.autoRotate = !this.autoRotate;
      if (this.controls) this.controls.autoRotate = this.autoRotate;
    },
    
    computeBaseBBox(modelScene) {
      // Get full bounding box first
      const fullBox = new THREE.Box3().setFromObject(modelScene);
      const fullSize = fullBox.getSize(new THREE.Vector3());
      const yThreshold = fullBox.min.y + fullSize.y * 0.1;

      // Collect all vertices in the lowest 10%
      const baseBox = new THREE.Box3();
      let found = false;
      modelScene.traverse((child) => {
        if (!child.isMesh) return;
        const pos = child.geometry.attributes.position;
        if (!pos) return;
        const matrix = child.matrixWorld;
        for (let i = 0; i < pos.count; i++) {
          const v = new THREE.Vector3().fromBufferAttribute(pos, i).applyMatrix4(matrix);
          if (v.y <= yThreshold) {
            baseBox.expandByPoint(v);
            found = true;
          }
        }
      });

      if (!found) return;

      const bCenter = baseBox.getCenter(new THREE.Vector3());
      const bSize = baseBox.getSize(new THREE.Vector3());

      this.baseBBox = {
        min: { x: baseBox.min.x, y: baseBox.min.y, z: baseBox.min.z },
        max: { x: baseBox.max.x, y: baseBox.max.y, z: baseBox.max.z },
        center: { x: bCenter.x, y: bCenter.y, z: bCenter.z },
        size: { x: bSize.x, y: bSize.y, z: bSize.z }
      };

      // Remove old helper if present
      if (this.baseBBoxHelper) {
        this.scene.remove(this.baseBBoxHelper);
        this.baseBBoxHelper.geometry.dispose();
      }

      // Draw the bounding box in the scene (bright yellow)
      this.baseBBoxHelper = new THREE.Box3Helper(baseBox, 0xffff00);
      this.scene.add(this.baseBBoxHelper);

      this.$emit('base-bbox', this.baseBBox);
    },

    addReferenceCube() {
      this.removeReferenceCube();
      this.removeFrontArrow();

      if (this.baseShape !== 'none') {
        const wUnit = this.baseWidth / 10;
        const lUnit = this.baseLength / 10;
        let geo;
        if (this.baseShape === 'round') {
          geo = new THREE.CylinderGeometry(0.5, 0.5, 0.3, 64);
        } else {
          geo = new THREE.BoxGeometry(wUnit, 0.3, lUnit);
        }

        const mat = new THREE.MeshStandardMaterial({
          color: 0x4a4a4a,
          roughness: 0.8,
          metalness: 0.1,
          transparent: true,
          opacity: 0.72
        });
        this.referenceCube = new THREE.Mesh(geo, mat);
        this.referenceCube.position.set(0, -0.15, 0);
        if (this.baseShape === 'round') {
          this.referenceCube.scale.set(wUnit, 1, lUnit);
        }
        this.scene.add(this.referenceCube);
      }

      this.addFrontArrow();
    },

    addFrontArrow() {
      this.removeFrontArrow();
      if (!this.model) return;

      const mat = new THREE.MeshBasicMaterial({ color: 0xffffff });

      const shaftLen = 0.55;
      const headLen  = 0.38;

      const shaftGeo = new THREE.CylinderGeometry(0.055, 0.055, shaftLen, 8);
      shaftGeo.applyMatrix4(new THREE.Matrix4().makeRotationX(Math.PI / 2));
      const shaftMesh = new THREE.Mesh(shaftGeo, mat);
      shaftMesh.position.set(0, 0, shaftLen / 2);

      const headGeo = new THREE.ConeGeometry(0.15, headLen, 8);
      headGeo.applyMatrix4(new THREE.Matrix4().makeRotationX(Math.PI / 2));
      const headMesh = new THREE.Mesh(headGeo, mat);
      headMesh.position.set(0, 0, shaftLen + headLen / 2);

      const group = new THREE.Group();
      group.add(shaftMesh);
      group.add(headMesh);

      // Place just beyond the front edge of the base; fall back to model bounds if no base
      let frontZ;
      if (this.baseShape !== 'none') {
        frontZ = (this.baseLength / 10) / 2 + 0.12;
      } else {
        const box = new THREE.Box3().setFromObject(this.model);
        frontZ = box.max.z + 0.12;
      }
      group.position.set(0, 0.06, frontZ);

      this.frontArrow = group;
      this.scene.add(group);
    },

    removeFrontArrow() {
      if (this.frontArrow) {
        this.scene.remove(this.frontArrow);
        this.frontArrow.traverse((child) => {
          if (child.isMesh) {
            child.geometry.dispose();
            child.material.dispose();
          }
        });
        this.frontArrow = null;
      }
    },

    removeReferenceCube() {
      if (this.referenceCube) {
        this.scene.remove(this.referenceCube);
        this.referenceCube.geometry.dispose();
        this.referenceCube.material.dispose();
        this.referenceCube = null;
      }
    },

    updateReferenceCube() {
      if (this.scene) {
        this.addReferenceCube();
        this.updateSizeRefCube();
      }
    },

    toggleSizeRef() {
      this.showSizeRef = !this.showSizeRef;
      this.updateSizeRefCube();
    },

    setSizeRef(scale) {
      if (this.showSizeRef && this.sizeRefScale === scale) {
        // clicking the active button toggles it off
        this.showSizeRef = false;
      } else {
        this.showSizeRef = true;
        this.sizeRefScale = scale;
      }
      this.updateSizeRefCube();
    },

    updateSizeRefCube() {
      this.removeSizeRefCube();
      if (this.showSizeRef && this.baseShape !== 'none') {
        this.addSizeRefCube();
      }
    },

    addSizeRefCube() {
      const scale = this.sizeRefScale || 1;
      // Dimensions per size tier: [width, height, length]
      const dims = scale === 3.5 ? [1.5, 3.5, 1.5]
                 : scale === 8   ? [3,   8,   3  ]
                 :                 [1,   2.5, 1  ]; // human (default)
      const [w, h, l] = dims;
      const geo = new THREE.BoxGeometry(w, h, l);
      const mat = new THREE.MeshStandardMaterial({
        color: 0xcccccc,
        roughness: 0.6,
        metalness: 0.1,
        transparent: true,
        opacity: 0.55
      });
      this.sizeRefCube = new THREE.Mesh(geo, mat);
      this.sizeRefCube.position.set(0, h / 2, 0);
      this.scene.add(this.sizeRefCube);
    },

    removeSizeRefCube() {
      if (this.sizeRefCube) {
        this.scene.remove(this.sizeRefCube);
        this.sizeRefCube.geometry.dispose();
        this.sizeRefCube.material.dispose();
        this.sizeRefCube = null;
      }
    },

    changeSetupShape(shape) {
      this.setupShape = shape;
      if (shape !== 'none' && this.setupPreset === 'custom') {
        // keep custom values as-is
      } else if (shape !== 'none') {
        this.setupPreset = 25;
        this.setupWidth = 25;
        this.setupLength = 25;
      }
    },

    selectPreset(val) {
      this.setupPreset = val;
      if (val !== 'custom') {
        this.setupWidth = val;
        this.setupLength = val;
      }
    },

    confirmBaseSetup() {
      this.baseWidth = this.setupWidth;
      this.baseLength = this.setupLength;
      if (this.setupSizeScale !== null) {
        this.sizeRefScale = this.setupSizeScale;
        this.showSizeRef = true;
        this.updateSizeRefCube();
      } else {
        this.showSizeRef = false;
        this.removeSizeRefCube();
      }
      this.showBaseSetupModal = false;
      this.setBaseShape(this.setupShape);
      try {
        localStorage.setItem('warhall_base_setup', JSON.stringify({
          setupShape: this.setupShape,
          setupPreset: this.setupPreset,
          setupWidth: this.setupWidth,
          setupLength: this.setupLength,
          setupSizeScale: this.setupSizeScale,
        }));
      } catch (e) { /* storage unavailable */ }
    },

    setBaseShape(shape) {
      this.baseShape = shape;
      if (shape === 'none') this.baseEditMode = false;
      this.updateReferenceCube();
    },

    toggleBaseEditMode() {
      const opening = !this.baseEditMode;
      this.closeAllPanels();
      if (opening) {
        this.pushHistory();
        this.baseEditMode = true;
      }
    },

    toggleSizeEditMode() {
      const opening = !this.sizeEditMode;
      this.closeAllPanels();
      if (opening) {
        this.pushHistory();
        this.sizeEditMode = true;
        if (this.controls) this.controls.mouseButtons.LEFT = -1;
        this.renderer.domElement.style.cursor = 'ns-resize';
        this._sizeMouseDownHandler = (e) => {
          if (e.button === 0) this.onSizeDragStart(e);
        };
        this._sizeTouchStartHandler = (e) => {
          e.preventDefault();
          this.onSizeDragStart(e);
        };
        this.renderer.domElement.addEventListener('mousedown', this._sizeMouseDownHandler);
        this.renderer.domElement.addEventListener('touchstart', this._sizeTouchStartHandler, { passive: false });
      }
    },

    onSizeDragStart(e) {
      if (!this.model) return;
      const startY = e.touches ? e.touches[0].clientY : e.clientY;
      const startScale = this.modelScale;
      const containerHeight = this.$refs.viewerContainer.clientHeight;
      const moveHandler = (ev) => {
        if (ev.cancelable) ev.preventDefault();
        const y = ev.touches ? ev.touches[0].clientY : ev.clientY;
        const deltaY = y - startY;
        // Dragging 50% of container height = ±1× the starting scale
        const newScale = Math.max(0.01, startScale * Math.pow(2, -(deltaY / containerHeight) * 2));
        this.modelScale = parseFloat(newScale.toFixed(3));
      };
      const upHandler = () => {
        window.removeEventListener('mousemove', moveHandler);
        window.removeEventListener('touchmove', moveHandler);
        window.removeEventListener('mouseup', upHandler);
        window.removeEventListener('touchend', upHandler);
      };
      window.addEventListener('mousemove', moveHandler);
      window.addEventListener('touchmove', moveHandler, { passive: false });
      window.addEventListener('mouseup', upHandler);
      window.addEventListener('touchend', upHandler);
    },

    togglePivotMode() {
      const opening = !this.pivotMode;
      this.closeAllPanels();
      if (opening) {
        this.pivotMode = true;
        // Disable left-click pan so controls don't react; right-click rotate still works
        if (this.controls) this.controls.mouseButtons.LEFT = -1;
        this._pivotMouseDownHandler = (e) => {
          if (e.button === 0) this.onPivotDragStart(e);
        };
        this._pivotTouchStartHandler = (e) => {
          e.preventDefault();
          this.onPivotDragStart(e);
        };
        this.renderer.domElement.addEventListener('mousedown', this._pivotMouseDownHandler);
        this.renderer.domElement.addEventListener('touchstart', this._pivotTouchStartHandler, { passive: false });
      }
    },

    recenterModel() {
      if (!this.model) return;
      this.model.updateMatrixWorld(true);
      const box = new THREE.Box3().setFromObject(this.model);
      const centerX = (box.min.x + box.max.x) / 2;
      const centerZ = (box.min.z + box.max.z) / 2;
      this.model.position.x -= centerX;
      this.model.position.z -= centerZ;
      this.updateReferenceCube();
    },

    screenToGroundPlane(clientX, clientY) {
      const rect = this.$refs.viewerContainer.getBoundingClientRect();
      const nx = ((clientX - rect.left) / rect.width) * 2 - 1;
      const ny = -((clientY - rect.top) / rect.height) * 2 + 1;
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(new THREE.Vector2(nx, ny), this.camera);
      const groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
      const target = new THREE.Vector3();
      return raycaster.ray.intersectPlane(groundPlane, target) ? target : null;
    },

    onPivotDragStart(e) {
      if (!this.model) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const startWorld = this.screenToGroundPlane(clientX, clientY);
      if (!startWorld) return;
      const startPosX = this.model.position.x;
      const startPosZ = this.model.position.z;

      const moveHandler = (ev) => {
        if (ev.cancelable) ev.preventDefault();
        const x = ev.touches ? ev.touches[0].clientX : ev.clientX;
        const y = ev.touches ? ev.touches[0].clientY : ev.clientY;
        const world = this.screenToGroundPlane(x, y);
        if (!world) return;
        this.model.position.x = startPosX + (world.x - startWorld.x);
        this.model.position.z = startPosZ + (world.z - startWorld.z);
      };
      const upHandler = () => {
        window.removeEventListener('mousemove', moveHandler);
        window.removeEventListener('touchmove', moveHandler);
        window.removeEventListener('mouseup', upHandler);
        window.removeEventListener('touchend', upHandler);
      };
      window.addEventListener('mousemove', moveHandler);
      window.addEventListener('touchmove', moveHandler, { passive: false });
      window.addEventListener('mouseup', upHandler);
      window.addEventListener('touchend', upHandler);
    },

    retryLoad() {
      this.loadModel();
    },

    closeAllPanels() {
      const wasPivot = this.pivotMode;
      const wasRotate = this.rotateMode;

      // Bake rotation into geometry so the exported model reflects the new orientation.
      if (wasRotate && this.model) {
        const rotEuler = this.model.rotation.clone();
        if (rotEuler.x !== 0 || rotEuler.y !== 0 || rotEuler.z !== 0) {
          this.pushHistory();
          const rotMatrix = new THREE.Matrix4().makeRotationFromEuler(rotEuler);
          this.model.traverse((child) => {
            if (!child.isMesh) return;
            child.geometry = child.geometry.clone();
            child.geometry.applyMatrix4(rotMatrix);
          });
          this.model.rotation.set(0, 0, 0);
          if (this.referenceCube) this.referenceCube.rotation.y = 0;
          // Track accumulated rotation for Link-mesh transform storage
          this.accumulatedRotationY += THREE.MathUtils.radToDeg(rotEuler.y);
          this.model.updateMatrixWorld(true);
          // Snap new bottom back to y=0
          const rBox = new THREE.Box3().setFromObject(this.model);
          if (Math.abs(rBox.min.y) > 0.001) {
            const localDy = -rBox.min.y / this.modelScale;
            const snapM = new THREE.Matrix4().makeTranslation(0, localDy, 0);
            this.model.traverse((child) => {
              if (!child.isMesh) return;
              child.geometry.applyMatrix4(snapM);
            });
            this.model.updateMatrixWorld(true);
          }
          this.modelHeight = new THREE.Box3().setFromObject(this.model).max.y;
        }
      }

      // When closing pivot mode, bake the XZ offset into geometry so the
      // model stays in world-space but model.position resets to (0,0,0).
      // The base is already at the correct reference position — don't snap it.
      if (wasPivot && this.model) {
        const dx = this.model.position.x;
        const dz = this.model.position.z;
        if (dx !== 0 || dz !== 0) {
          this.pushHistory();
          const bakeMatrix = new THREE.Matrix4().makeTranslation(dx / this.modelScale, 0, dz / this.modelScale);
          this.model.traverse((child) => {
            if (!child.isMesh) return;
            child.geometry = child.geometry.clone();
            child.geometry.applyMatrix4(bakeMatrix);
          });
          this.model.position.x = 0;
          this.model.position.z = 0;
          // Track accumulated offset for Link-mesh transform storage
          this.accumulatedOffsetX += dx;
          this.accumulatedOffsetZ += dz;
          this.model.updateMatrixWorld(true);
        }
      }

      this.cutMode = false;
      this.baseEditMode = false;
      this.sizeEditMode = false;
      this.pivotMode = false;
      this.rotateMode = false;
      this.hideCutPlane();
      if (this.controls) {
        this.controls.enabled = true;
        this.controls.mouseButtons.LEFT = THREE.MOUSE.PAN;
      }
      if (this._pivotMouseDownHandler) {
        this.renderer.domElement.removeEventListener('mousedown', this._pivotMouseDownHandler);
        this._pivotMouseDownHandler = null;
      }
      if (this._pivotTouchStartHandler) {
        this.renderer.domElement.removeEventListener('touchstart', this._pivotTouchStartHandler);
        this._pivotTouchStartHandler = null;
      }
      if (this._cutMouseDownHandler) {
        this.renderer.domElement.removeEventListener('mousedown', this._cutMouseDownHandler);
        this._cutMouseDownHandler = null;
      }
      if (this._cutTouchStartHandler) {
        this.renderer.domElement.removeEventListener('touchstart', this._cutTouchStartHandler);
        this._cutTouchStartHandler = null;
      }
      if (this._sizeMouseDownHandler) {
        this.renderer.domElement.removeEventListener('mousedown', this._sizeMouseDownHandler);
        this._sizeMouseDownHandler = null;
      }
      if (this._sizeTouchStartHandler) {
        this.renderer.domElement.removeEventListener('touchstart', this._sizeTouchStartHandler);
        this._sizeTouchStartHandler = null;
      }
      if (this._rotateMouseDownHandler) {
        this.renderer.domElement.removeEventListener('mousedown', this._rotateMouseDownHandler);
        this._rotateMouseDownHandler = null;
      }
      if (this._rotateTouchStartHandler) {
        this.renderer.domElement.removeEventListener('touchstart', this._rotateTouchStartHandler);
        this._rotateTouchStartHandler = null;
      }
      this.renderer.domElement.style.cursor = '';

      // Re-snap base only when not coming from pivot mode.
      // In pivot mode the base is already at the user-chosen reference position.
      if (!wasPivot) {
        this.updateReferenceCube();
      }
    },

    toggleRotateMode() {
      const opening = !this.rotateMode;
      this.closeAllPanels();
      if (opening) {
        this.rotateYAngle = 0;
        this.rotateMode = true;
        if (this.controls) this.controls.mouseButtons.LEFT = -1;
        this.renderer.domElement.style.cursor = 'ew-resize';
        this._rotateMouseDownHandler = (e) => {
          if (e.button === 0) this.onRotateDragStart(e);
        };
        this._rotateTouchStartHandler = (e) => {
          e.preventDefault();
          this.onRotateDragStart(e);
        };
        this.renderer.domElement.addEventListener('mousedown', this._rotateMouseDownHandler);
        this.renderer.domElement.addEventListener('touchstart', this._rotateTouchStartHandler, { passive: false });
      }
    },

    onRotateDragStart(e) {
      if (!this.model) return;
      const startX = e.touches ? e.touches[0].clientX : e.clientX;
      const startAngle = this.rotateYAngle;
      const containerWidth = this.$refs.viewerContainer.clientWidth;
      const moveHandler = (ev) => {
        if (ev.cancelable) ev.preventDefault();
        const x = ev.touches ? ev.touches[0].clientX : ev.clientX;
        const deltaDeg = -((x - startX) / containerWidth) * 360;
        this.rotateYAngle = Math.max(-180, Math.min(180, startAngle + deltaDeg));
      };
      const upHandler = () => {
        window.removeEventListener('mousemove', moveHandler);
        window.removeEventListener('touchmove', moveHandler);
        window.removeEventListener('mouseup', upHandler);
        window.removeEventListener('touchend', upHandler);
      };
      window.addEventListener('mousemove', moveHandler);
      window.addEventListener('touchmove', moveHandler, { passive: false });
      window.addEventListener('mouseup', upHandler);
      window.addEventListener('touchend', upHandler);
    },

    resetModelRotation() {
      if (this.model) {
        this.model.rotation.set(0, 0, 0);
        if (this.referenceCube) this.referenceCube.rotation.y = 0;
        this.rotateYAngle = 0;
      }
    },

    toggleCutMode() {
      const opening = !this.cutMode;
      this.closeAllPanels();
      if (opening) {
        this.cutMode = true;
        this.showCutPlane();
        // Disable left-click pan; right-click rotate remains active
        if (this.controls) this.controls.mouseButtons.LEFT = -1;
        this.renderer.domElement.style.cursor = 'ns-resize';
        this._cutMouseDownHandler = (e) => {
          if (e.button === 0) this.onCutDragStart(e);
        };
        this._cutTouchStartHandler = (e) => {
          e.preventDefault();
          this.onCutDragStart(e);
        };
        this.renderer.domElement.addEventListener('mousedown', this._cutMouseDownHandler);
        this.renderer.domElement.addEventListener('touchstart', this._cutTouchStartHandler, { passive: false });
      }
    },

    onCutDragStart(e) {
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      this._cutDragStartY = clientY;
      this._cutDragStartPercent = this.cutPercent;
      const moveHandler = (ev) => {
        if (ev.cancelable) ev.preventDefault();
        const y = ev.touches ? ev.touches[0].clientY : ev.clientY;
        const containerHeight = this.$refs.viewerContainer.clientHeight;
        const deltaPercent = ((this._cutDragStartY - y) / containerHeight) * 30;
        this.cutPercent = Math.min(30, Math.max(0, this._cutDragStartPercent + deltaPercent));
        this.updateCutPlane();
      };
      const upHandler = () => {
        window.removeEventListener('mousemove', moveHandler);
        window.removeEventListener('touchmove', moveHandler);
        window.removeEventListener('mouseup', upHandler);
        window.removeEventListener('touchend', upHandler);
      };
      window.addEventListener('mousemove', moveHandler);
      window.addEventListener('touchmove', moveHandler, { passive: false });
      window.addEventListener('mouseup', upHandler);
      window.addEventListener('touchend', upHandler);
    },

    showCutPlane() {
      this.hideCutPlane();
      const cutY = (this.cutPercent / 100) * this.modelHeight;
      const geo = new THREE.PlaneGeometry(18, 18);
      const mat = new THREE.MeshBasicMaterial({
        color: 0x60a5fa,
        transparent: true,
        opacity: 0.35,
        side: THREE.DoubleSide,
        depthWrite: false
      });
      this.cutPlaneHelper = new THREE.Mesh(geo, mat);
      this.cutPlaneHelper.rotation.x = -Math.PI / 2;
      this.cutPlaneHelper.position.y = cutY;
      this.scene.add(this.cutPlaneHelper);
    },

    hideCutPlane() {
      if (this.cutPlaneHelper) {
        this.scene.remove(this.cutPlaneHelper);
        this.cutPlaneHelper.geometry.dispose();
        this.cutPlaneHelper.material.dispose();
        this.cutPlaneHelper = null;
      }
    },

    updateCutPlane() {
      const cutY = (this.cutPercent / 100) * this.modelHeight;
      if (this.cutPlaneHelper) this.cutPlaneHelper.position.y = cutY;
    },

    applyCut() {
      if (!this.model) return;
      const cutY = (this.cutPercent / 100) * this.modelHeight;
      this.model.updateMatrixWorld(true);

      this.pushHistory();

      this.model.traverse((child) => {
        if (!child.isMesh || child.isSkinnedMesh) return;
        child.updateMatrixWorld(true);
        const newGeom = this.cutMeshGeometry(child.geometry, child.matrixWorld, cutY);
        if (newGeom) {
          child.geometry.dispose();
          child.geometry = newGeom;
        }
      });
      this.hideCutPlane();
      this.cutMode = false;
      if (this.controls) this.controls.mouseButtons.LEFT = THREE.MOUSE.PAN;
      if (this._cutMouseDownHandler) {
        this.renderer.domElement.removeEventListener('mousedown', this._cutMouseDownHandler);
        this._cutMouseDownHandler = null;
      }
      if (this._cutTouchStartHandler) {
        this.renderer.domElement.removeEventListener('touchstart', this._cutTouchStartHandler);
        this._cutTouchStartHandler = null;
      }
      this.renderer.domElement.style.cursor = '';
      // Update modelHeight after cut, snapping the new bottom to y=0
      this.model.updateMatrixWorld(true);
      const newBox = new THREE.Box3().setFromObject(this.model);
      if (newBox.min.y > 0.001) {
        // Translate geometry in local space so the cut face lands at world y=0
        const localDy = -newBox.min.y / this.modelScale;
        const snapMatrix = new THREE.Matrix4().makeTranslation(0, localDy, 0);
        this.model.traverse((child) => {
          if (!child.isMesh) return;
          child.geometry.applyMatrix4(snapMatrix);
        });
        this.model.updateMatrixWorld(true);
        this.modelHeight = new THREE.Box3().setFromObject(this.model).max.y;
      } else {
        this.modelHeight = newBox.max.y;
      }
      // this.computeBaseBBox(this.model);
      this.updateReferenceCube();
    },

    undoAction() {
      const entry = this.actionHistory.pop();
      if (!entry) return;
      if (this.model) {
        entry.geometrySnapshot.forEach(({ mesh, geometry }) => {
          mesh.geometry.dispose();
          mesh.geometry = geometry;
        });
        this.model.rotation.set(0, 0, 0);
        this.model.position.set(0, 0, 0);
        this.model.updateMatrixWorld(true);
      }
      this.modelHeight = entry.modelHeight;
      // Restore scalar properties before triggering watchers
      this.baseShape = entry.baseShape;
      this.baseWidth = entry.baseWidth;
      this.baseLength = entry.baseLength;
      this.modelScale = entry.modelScale; // watcher applies scale to model
      this.updateReferenceCube();
    },

    pushHistory() {
      if (!this.model) return;
      const geometrySnapshot = [];
      this.model.traverse((child) => {
        if (!child.isMesh || child.isSkinnedMesh) return;
        geometrySnapshot.push({ mesh: child, geometry: child.geometry.clone() });
      });
      this.actionHistory.push({
        geometrySnapshot,
        modelHeight: this.modelHeight,
        modelScale: this.modelScale,
        baseShape: this.baseShape,
        baseWidth: this.baseWidth,
        baseLength: this.baseLength,
      });
    },

    cutMeshGeometry(geometry, matrixWorld, cutYWorld) {
      // Transform cut plane into mesh local space
      const invMat = new THREE.Matrix4().copy(matrixWorld).invert();
      const planeNormal = new THREE.Vector3(0, 1, 0).transformDirection(invMat).normalize();
      const planePoint  = new THREE.Vector3(0, cutYWorld, 0).applyMatrix4(invMat);
      const planeD      = planePoint.dot(planeNormal);
      const sdf = p => p.dot(planeNormal) - planeD;

      const g   = geometry.toNonIndexed();
      const pos = g.attributes.position;
      const nor = g.attributes.normal;
      const uv  = g.attributes.uv;

      const getV = i => ({
        p: new THREE.Vector3().fromBufferAttribute(pos, i),
        n: nor ? new THREE.Vector3().fromBufferAttribute(nor, i) : new THREE.Vector3(0, 1, 0),
        u: uv ? uv.getX(i) : 0,
        v: uv ? uv.getY(i) : 0,
      });
      // Linearly interpolate a vertex at the plane boundary, preserving UVs
      const lerpV = (a, b, t) => ({
        p: a.p.clone().lerp(b.p, t),
        n: a.n.clone().lerp(b.n, t).normalize(),
        u: a.u + (b.u - a.u) * t,
        v: a.v + (b.v - a.v) * t,
      });

      const outPos = [], outNor = [], outUv = [];
      const pushV = v => {
        outPos.push(v.p.x, v.p.y, v.p.z);
        outNor.push(v.n.x, v.n.y, v.n.z);
        if (uv) outUv.push(v.u, v.v);
      };

      for (let i = 0; i < pos.count; i += 3) {
        let vs = [getV(i), getV(i + 1), getV(i + 2)];
        let ds = vs.map(v => sdf(v.p));
        const above = ds.filter(d => d >= 0).length;

        if (above === 3) {
          // Fully above — keep as-is
          vs.forEach(pushV);
        } else if (above === 0) {
          // Fully below — discard
        } else if (above === 2) {
          // One vertex below — rotate until the below vertex is at index 2
          while (ds[2] >= 0) { vs = [vs[1], vs[2], vs[0]]; ds = [ds[1], ds[2], ds[0]]; }
          const t0 = ds[0] / (ds[0] - ds[2]);
          const t1 = ds[1] / (ds[1] - ds[2]);
          const c0 = lerpV(vs[0], vs[2], t0);
          const c1 = lerpV(vs[1], vs[2], t1);
          // Clipped quad → two triangles, preserving original winding
          pushV(vs[0]); pushV(vs[1]); pushV(c0);
          pushV(vs[1]); pushV(c1);    pushV(c0);
        } else {
          // Two vertices below — rotate until the above vertex is at index 0
          while (ds[0] < 0) { vs = [vs[1], vs[2], vs[0]]; ds = [ds[1], ds[2], ds[0]]; }
          const t0 = ds[0] / (ds[0] - ds[1]);
          const t1 = ds[0] / (ds[0] - ds[2]);
          const c0 = lerpV(vs[0], vs[1], t0);
          const c1 = lerpV(vs[0], vs[2], t1);
          pushV(vs[0]); pushV(c0); pushV(c1);
        }
      }

      const newGeom = new THREE.BufferGeometry();
      newGeom.setAttribute('position', new THREE.Float32BufferAttribute(outPos, 3));
      newGeom.setAttribute('normal',   new THREE.Float32BufferAttribute(outNor, 3));
      if (uv) newGeom.setAttribute('uv', new THREE.Float32BufferAttribute(outUv, 2));
      return mergeVertices(newGeom, 1e-4);
    },

    captureSnapshot(width = 512, height = 512) {
      return new Promise((resolve) => {
        if (!this.scene || !this.perspCamera) { resolve(null); return; }

        // Hide base and helper objects
        const toHide = [this.referenceCube, this.sizeRefCube, this.baseBBoxHelper, this.cutPlaneHelper, this.frontArrow].filter(Boolean);
        toHide.forEach(o => { o.visible = false; });

        // Save camera state
        const savedAspect = this.perspCamera.aspect;
        const savedPos = this.perspCamera.position.clone();
        const savedQuat = this.perspCamera.quaternion.clone();

        // Fit camera to model for a tight, well-framed snapshot
        if (this.model) {
          const box = new THREE.Box3().setFromObject(this.model);
          const center = box.getCenter(new THREE.Vector3());
          const size = box.getSize(new THREE.Vector3());
          const maxDim = Math.max(size.x, size.y, size.z);
          const fov = this.perspCamera.fov * (Math.PI / 180);
          // 1.2× gives a snug frame; push slightly right and above for a 3/4 view
          const distance = (maxDim / 2) / Math.tan(fov / 2) * 1.2;
          this.perspCamera.position.set(
            center.x + distance * 0.35,
            center.y + distance * 0.25,
            center.z + distance
          );
          this.perspCamera.lookAt(center);
        }

        this.perspCamera.aspect = width / height;
        this.perspCamera.updateProjectionMatrix();

        // Offscreen renderer
        const offscreen = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
        offscreen.setSize(width, height);
        offscreen.setPixelRatio(1);
        offscreen.render(this.scene, this.perspCamera);
        const dataUrl = offscreen.domElement.toDataURL('image/jpeg', 0.85);
        offscreen.dispose();

        // Restore camera
        this.perspCamera.position.copy(savedPos);
        this.perspCamera.quaternion.copy(savedQuat);
        this.perspCamera.aspect = savedAspect;
        this.perspCamera.updateProjectionMatrix();
        toHide.forEach(o => { o.visible = true; });

        resolve(dataUrl);
      });
    },

    // Returns the current viewer transforms for Link meshes.
    // Call AFTER closeAllPanels() — accumulated values include all baked edits.
    //
    // SkinLayerSize derivation:
    //   Uploaded GLB: geometry baked at (8/originalMaxDim), root node scale = EXPORT_SCALE = (originalMaxDim/8)*modelScale*MODEL_SCALE_FACTOR
    //   Net vertex in game = EXPORT_SCALE * (8/originalMaxDim) * v = modelScale*MODEL_SCALE_FACTOR * v  (originalMaxDim cancels)
    //   → game sees modelScale*MODEL_SCALE_FACTOR regardless of originalMaxDim, with SkinLayerSize=1.0
    //
    //   Linked: game applies SkinLayerSize to raw original vertices directly
    //   → SkinLayerSize = modelScale * MODEL_SCALE_FACTOR  (the (originalMaxDim/8) factor must NOT be included)
    getModelTransforms() {
      // ×MODEL_SCALE_FACTOR matches the EXPORT_SCALE calibration. ÷2.54 cancels Unity's DEFAULT_SIZE ×2.54 multiplier.
      // Net Unity size = modelScale × MODEL_SCALE_FACTOR, matching uploaded GLBs.
      const skinLayerSize = this.modelScale * MODEL_SCALE_FACTOR / 2.54;
      return {
        scale:     skinLayerSize,
        rotationY: this.accumulatedRotationY,
        positionX: this.accumulatedOffsetX,
        positionZ: this.accumulatedOffsetZ,
      };
    },

    // Bake all texture images on the model into canvas elements so GLTFExporter
    // can reliably read pixel data regardless of source format (FBX blob URLs, etc.)
    async _prepareTexturesForExport() {
      if (!this.model) return;

      // FBXLoader creates MeshPhongMaterial / MeshLambertMaterial. GLTFExporter is built around
      // MeshStandardMaterial and silently drops textures on unknown material types.
      // Convert before export so all maps are preserved correctly.
      if (this.sourceFormat === 'fbx') {
        this.model.traverse((child) => {
          if (!child.isMesh || !child.material) return;
          const mats = Array.isArray(child.material) ? child.material : [child.material];
          const converted = mats.map((mat) => {
            if (
              !(mat instanceof THREE.MeshPhongMaterial) &&
              !(mat instanceof THREE.MeshLambertMaterial)
            ) return mat;
            const std = new THREE.MeshStandardMaterial({
              color:             mat.color.clone(),
              map:               mat.map          || null,
              normalMap:         mat.normalMap     || null,
              emissiveMap:       mat.emissiveMap   || null,
              aoMap:             mat.aoMap         || null,
              alphaMap:          mat.alphaMap      || null,
              transparent:       mat.transparent,
              opacity:           mat.opacity,
              side:              mat.side,
              emissive:          mat.emissive ? mat.emissive.clone() : new THREE.Color(0x000000),
              emissiveIntensity: mat.emissiveIntensity != null ? mat.emissiveIntensity : 1.0,
              roughness:         1.0,
              metalness:         0.0,
            });
            mat.dispose();
            return std;
          });
          child.material = Array.isArray(child.material) ? converted : converted[0];
        });

        // FBX scenes have a complex hierarchy: FBXLoader inserts intermediate Group/skeleton
        // nodes that carry unit-correction scales (e.g. 0.01 for cm→m, or inverse 100).
        // _placeModelInScene only resets MESH node transforms; the intermediate nodes survive
        // with their original transforms. GLTFExporter writes all node transforms into the GLB,
        // so Unity multiplies them in and the model ends up 100× the wrong size.
        // Fix: after geometry is baked into world space, flatten the hierarchy by reparenting
        // all meshes directly to the root and discarding intermediate Group nodes.
        const meshes = [];
        this.model.traverse((child) => { if (child.isMesh) meshes.push(child); });
        // Detach every child of root (clears the entire FBX hierarchy)
        while (this.model.children.length > 0) this.model.remove(this.model.children[0]);
        // Re-add only the meshes — their geometry is already baked, local transforms are identity
        meshes.forEach(mesh => this.model.add(mesh));
      }

      const SLOTS = ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'emissiveMap', 'aoMap', 'alphaMap'];
      const seen = new Set();
      const promises = [];
      this.model.traverse((child) => {
        if (!child.isMesh || !child.material) return;
        const mats = Array.isArray(child.material) ? child.material : [child.material];
        mats.forEach((mat) => {
          SLOTS.forEach((slot) => {
            const tex = mat[slot];
            if (!tex || !tex.image || seen.has(tex.uuid)) return;
            seen.add(tex.uuid);
            const img = tex.image;
            // Already a canvas — nothing to do
            if (img instanceof HTMLCanvasElement || img instanceof OffscreenCanvas) return;
            const applyCanvas = () => {
              const w = img.naturalWidth || img.width || 512;
              const h = img.naturalHeight || img.height || 512;
              if (w === 0 || h === 0) return;
              const canvas = document.createElement('canvas');
              canvas.width = w;
              canvas.height = h;
              const ctx = canvas.getContext('2d');
              try {
                ctx.drawImage(img, 0, 0, w, h);
                tex.image = canvas;
                tex.needsUpdate = true;
              } catch (e) {
                console.warn('Could not bake texture to canvas (tainted?), skipping:', e);
              }
            };
            if (img instanceof HTMLImageElement) {
              if (img.complete && img.naturalWidth > 0) {
                applyCanvas();
              } else {
                promises.push(new Promise((res) => {
                  img.onload = () => { applyCanvas(); res(); };
                  img.onerror = res;
                }));
              }
            } else if (img instanceof ImageBitmap) {
              applyCanvas();
            }
          });
        });
      });
      await Promise.all(promises);

      // OBJ files store UVs with V=0 at bottom (OpenGL convention). GLTF spec uses V=0 at top.
      // Three.js compensates via texture.flipY + KHR_texture_transform in the export,
      // but Unity doesn't support KHR_texture_transform, so UVs appear flipped.
      // Fix: flip V-coords in geometry and clear flipY so the exported GLTF has correct UVs.
      // FBX is exempt: FBXLoader already pre-flips V during load, so no second flip is needed.
      if (this.sourceFormat === 'obj') {
        const SLOTS = ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'emissiveMap', 'aoMap', 'alphaMap'];
        const texToFix = new Set();
        this.model.traverse((child) => {
          if (!child.isMesh) return;
          // Collect flipY textures on this mesh
          if (child.material) {
            const mats = Array.isArray(child.material) ? child.material : [child.material];
            mats.forEach((mat) => {
              SLOTS.forEach((slot) => {
                const tex = mat[slot];
                if (tex && tex.flipY) texToFix.add(tex);
              });
            });
          }
          // Flip V on all UV sets
          ['uv', 'uv2'].forEach((uvName) => {
            const uvAttr = child.geometry.attributes[uvName];
            if (!uvAttr) return;
            for (let i = 0; i < uvAttr.count; i++) {
              uvAttr.setY(i, 1.0 - uvAttr.getY(i));
            }
            uvAttr.needsUpdate = true;
          });
        });
        texToFix.forEach((tex) => { tex.flipY = false; tex.needsUpdate = true; });
      }
    },

    // applyUserScale: when false, exports at base normalisation scale only (user scale stored in DB).
    async exportGLB({ applyUserScale = true } = {}) {
      await this._prepareTexturesForExport();
      return new Promise((resolve, reject) => {
        if (!this.model) {
          reject(new Error('No model loaded'));
          return;
        }
        const exporter = new GLTFExporter();
        // FBXLoader stores raw FBX vertex coordinates (no unit conversion).
        // Blender exports FBX in cm, so originalMaxDim is 100× the metre value.
        // OBJ from tools other than Blender may also be in cm — same heuristic.
        // GLB is always in metres so no correction needed.
        let refDim = this.originalMaxDim;
        if (this.sourceFormat === 'fbx' || this.sourceFormat === 'obj') {
          if      (refDim > 1000) refDim /= 1000; // mm → m
          else if (refDim > 10)   refDim /= 100;  // cm → m (Blender / Maya / 3ds Max default)
        }
        // ÷2.54: Unity applies DEFAULT_SIZE=2.54 (inch→cm) to all loaded models.
        // Pre-dividing cancels that factor so net Unity size = modelScale × 3.
        // Exception: 'Created' (TripoAI) models intentionally retain the ×2.54 factor
        // so they are exported 2.54× larger to compensate for their native scale.
        const isCreated = this.modelData && this.modelData.source === 'Created';
        const EXPORT_SCALE = (refDim / 8) * this.modelScale * MODEL_SCALE_FACTOR / (isCreated ? 1 : 2.54);
        const originalScale = this.model.scale.clone();
        this.model.scale.setScalar(EXPORT_SCALE);
        exporter.parse(
          this.model,
          (glb) => {
            this.model.scale.copy(originalScale);
            resolve(new Blob([glb], { type: 'model/gltf-binary' }));
          },
          (error) => {
            this.model.scale.copy(originalScale);
            reject(error);
          },
          { binary: true }
        );
      });
    },

    wrapUpModel() {
      if (!this.model) return;

      // Bake all world transforms into each mesh's geometry and reset transforms
      this.model.updateMatrixWorld(true);
      this.model.traverse((child) => {
        if (!child.isMesh) return;
        child.geometry = child.geometry.clone();
        child.geometry.applyMatrix4(child.matrixWorld);
        child.position.set(0, 0, 0);
        child.rotation.set(0, 0, 0);
        child.scale.set(1, 1, 1);
        child.updateMatrix();
      });

      // Reset root model transforms
      this.model.position.set(0, 0, 0);
      this.model.rotation.set(0, 0, 0);
      this.model.scale.set(1, 1, 1);
      this.model.updateMatrixWorld(true);

      // Compute bounding box and translate geometry so origin = center-bottom
      const box = new THREE.Box3().setFromObject(this.model);
      const centerX = (box.min.x + box.max.x) / 2;
      const minY = box.min.y;
      const centerZ = (box.min.z + box.max.z) / 2;
      const offset = new THREE.Matrix4().makeTranslation(-centerX, -minY, -centerZ);

      this.model.traverse((child) => {
        if (!child.isMesh) return;
        child.geometry.applyMatrix4(offset);
      });

      this.model.updateMatrixWorld(true);

      // Update helpers
      const newBox = new THREE.Box3().setFromObject(this.model);
      this.modelHeight = newBox.max.y;
      // this.computeBaseBBox(this.model);

      // Refit camera to the transformed model
      this.fitCameraToModel();
    },

    fitCameraToModel() {
      if (!this.model) return;
      const box = new THREE.Box3().setFromObject(this.model);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      const fov = this.camera.fov * (Math.PI / 180);
      const distance = (maxDim / 2) / Math.tan(fov / 2) * 1.8;

      this.controls.target.set(center.x, center.y, center.z);
      this.camera.position.set(center.x, center.y, center.z + distance);
      this.controls.minDistance = distance * 0.1;
      this.controls.maxDistance = distance * 15;
      this.controls.update();
    }
  }
};
</script>

<style scoped>
.model-viewer {
  position: relative;
  width: 100%;
  height: 600px;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}

.viewer-container {
  width: 100%;
  height: 100%;
  touch-action: none;
}

.viewer-loading,
.viewer-error {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: white;
  z-index: 10;
}

.viewer-loading .spinner {
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid white;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
  margin: 0 auto 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.viewer-loading p,
.viewer-error p {
  font-size: 1.2em;
  margin-bottom: 15px;
}

.viewer-error button {
  background: #e74c3c;
  color: white;
  border: none;
  padding: 10px 25px;
  border-radius: 20px;
  cursor: pointer;
  font-size: 1em;
}



.cut-drag-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 5;
  pointer-events: none;
}

/* ── Cut panel specific ── */
.apply-cut-btn {
  background: #27ae60;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 5px 12px;
  cursor: pointer;
  font-size: 0.9em;
}

.apply-cut-btn:hover { background: #2ecc71; }

.undo-cut-btn {
  width: 34px;
  height: 34px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1em;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.3s;
}

.undo-cut-btn:hover { background: #2563eb; }

.ctrl-group {
  position: absolute;
  bottom: 15px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 2px;
  z-index: 7;
  background: rgba(0, 0, 0, 0.65);
  padding: 5px 8px;
  border-radius: 20px;
}

.ctrl-group--left {
  left: 15px;
}

.ctrl-group--right {
  right: 15px;
}

.ctrl-group-icon {
  font-size: 1em;
  opacity: 0.75;
  padding-right: 6px;
  border-right: 1px solid rgba(255, 255, 255, 0.2);
  margin-right: 2px;
  line-height: 1;
  cursor: pointer;
  user-select: none;
}

.ctrl-group--collapsed .ctrl-group-icon {
  border-right: none;
  padding-right: 0;
  margin-right: 0;
  opacity: 1;
}

.ctrl-group button:not(.undo-cut-btn) {
  background: transparent;
  color: white;
  border: none;
  padding: 0 11px;
  height: 34px;
  border-radius: 14px;
  cursor: pointer;
  font-size: 0.9em;
  transition: background 0.3s;
  white-space: nowrap;
  display: flex;
  align-items: center;
}

.ctrl-group button:not(.undo-cut-btn):hover {
  background: rgba(255, 255, 255, 0.15);
}

.ctrl-group button:not(.undo-cut-btn).active {
  background: #1d4ed8;
  color: white;
}

/* ── Base shape toggle ── */
.base-controls-stack {
  position: absolute;
  top: 15px;
  left: 15px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  z-index: 5;
}

.base-shape-toggle {
  display: flex;
  gap: 3px;
  background: rgba(0, 0, 0, 0.55);
  padding: 3px;
  border-radius: 7px;
}

.base-shape-toggle button {
  width: 24px;
  height: 24px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.7em;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  line-height: 1;
}

.base-shape-toggle button:hover {
  background: rgba(255, 255, 255, 0.2);
}

.base-shape-toggle button.active {
  background: #3b82f6;
  border-color: #3b82f6;
}

@media (max-width: 768px) {
  .model-viewer {
    height: 400px;
  }

  /* Camera group — top-left, vertical */
  .ctrl-group--left {
    top: 10px;
    left: 6px;
    bottom: auto;
    flex-direction: column;
    align-items: flex-start;
    border-radius: 12px;
    padding: 6px 5px;
  }

  /* Tool group — top-right, vertical */
  .ctrl-group--right {
    top: 10px;
    right: 6px;
    bottom: auto;
    flex-direction: column;
    align-items: flex-end;
    border-radius: 12px;
    padding: 6px 5px;
  }

  /* Icon separator becomes a bottom border instead of right border */
  .ctrl-group-icon {
    border-right: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    padding-right: 0;
    padding-bottom: 4px;
    margin-right: 0;
    margin-bottom: 2px;
    width: 100%;
    text-align: center;
  }

  .ctrl-group--collapsed .ctrl-group-icon {
    border-bottom: none;
    padding-bottom: 0;
    margin-bottom: 0;
  }

  .ctrl-group button:not(.undo-cut-btn) {
    padding: 5px 8px;
    font-size: 0.78em;
    height: auto;
    width: 100%;
    justify-content: flex-start;
  }

  /* Base controls stack — top-center on mobile */
  .base-controls-stack {
    top: 10px;
    left: 50%;
    transform: translateX(-50%);
  }

  /* Tool panels — bottom-right */
  .tool-panel {
    bottom: 12px;
    right: 6px;
    left: auto;
    transform: none;
    white-space: nowrap;
  }

  .tool-panel--vertical {
    white-space: normal;
    min-width: 180px;
    transform: none;
  }
}

/* ── Shared tool panel ── */
.tool-panel {
  position: absolute;
  bottom: 67px;
  right: 15px;
  background: rgba(0, 0, 0, 0.75);
  color: white;
  padding: 8px 10px;
  border-radius: 10px;
  z-index: 6;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85em;
}

.tool-panel-label {
  font-weight: bold;
  white-space: nowrap;
}

.tool-panel-content {
  display: flex;
  align-items: center;
  gap: 10px;
}

.panel-close {
  width: 24px;
  height: 24px;
  background: #c0392b;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.8em;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0;
}

.panel-close:hover { background: #e74c3c; }

.base-size-field {
  display: flex;
  align-items: center;
  gap: 5px;
  line-height: 1;
}

.base-size-field input {
  width: 58px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  color: white;
  padding: 4px 6px;
  font-size: 0.95em;
  text-align: center;
  -moz-appearance: textfield;
}

.base-size-field input::-webkit-outer-spin-button,
.base-size-field input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.base-size-close { display: none; }

.base-size-unit {
  opacity: 0.7;
  font-size: 0.85em;
  white-space: nowrap;
}

/* ── Vertical tool panel variant ── */
.tool-panel--vertical {
  flex-direction: column;
  align-items: stretch;
  min-width: 150px;
}

.tool-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.tool-panel--vertical .tool-panel-content {
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
}

.tool-panel--vertical .size-slider {
  width: 100%;
  box-sizing: border-box;
}

.tool-panel--vertical .size-value-input {
  width: 100%;
  box-sizing: border-box;
  text-align: left;
}

/* ── Size panel ── */
.size-value-input {
  width: 58px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  color: white;
  padding: 4px 6px;
  font-size: 0.95em;
  text-align: center;
  -moz-appearance: textfield;
}

.size-value-input::-webkit-outer-spin-button,
.size-value-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.size-slider {
  width: 120px;
  accent-color: #3b82f6;
  cursor: pointer;
}

/* ── Pivot / center panel ── */
.pivot-hint {
  white-space: nowrap;
  opacity: 0.8;
}

.pivot-recenter-btn {
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 4px 10px;
  cursor: pointer;
  font-size: 0.9em;
}

.pivot-recenter-btn:hover { background: #2563eb; }

/* Base Setup Modal */
.base-setup-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.base-setup-modal {
  background: #1e2030;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 14px;
  padding: 24px 28px;
  min-width: 280px;
  max-width: 340px;
  width: 90%;
  color: #e8eaf0;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}

.base-setup-title {
  margin: 0 0 4px;
  font-size: 1.15em;
  font-weight: 600;
  color: #fff;
}

.base-setup-subtitle {
  margin: 0 0 18px;
  font-size: 0.82em;
  color: rgba(255, 255, 255, 0.5);
}

.base-setup-section-label {
  margin: 0 0 8px;
  font-size: 0.82em;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.base-setup-shapes {
  display: flex;
  gap: 8px;
  margin-bottom: 18px;
}

.base-setup-size-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}

.base-setup-select {
  flex: 1;
  padding: 6px 8px;
  border-radius: 7px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.08);
  color: #e8eaf0;
  font-size: 0.88em;
  cursor: pointer;
}

.base-setup-select option {
  background: #1e2030;
  color: #e8eaf0;
}

.base-setup-shape-btn {
  flex: 1;
  padding: 8px 4px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
  color: #e8eaf0;
  font-size: 0.82em;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.base-setup-shape-btn.active {
  background: #3b82f6;
  border-color: #3b82f6;
  color: #fff;
}

.base-setup-shape-btn:hover:not(.active) {
  background: rgba(255, 255, 255, 0.12);
}

.base-setup-size {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
}

.base-setup-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.base-setup-preset-option {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 11px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
  color: #e8eaf0;
  font-size: 0.82em;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  user-select: none;
}

.base-setup-preset-option input[type="radio"] {
  display: none;
}

.base-setup-preset-option.active {
  background: #3b82f6;
  border-color: #3b82f6;
  color: #fff;
}

.base-setup-preset-option:hover:not(.active) {
  background: rgba(255, 255, 255, 0.12);
}

.base-setup-custom-fields {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.base-setup-field {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.88em;
}

.base-setup-field-label {
  width: 48px;
  color: rgba(255, 255, 255, 0.6);
}

.base-setup-field input[type="number"] {
  flex: 1;
  padding: 5px 8px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  font-size: 1em;
  text-align: center;
}

.base-setup-unit {
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.82em;
  width: 24px;
}

.base-setup-actions {
  display: flex;
  justify-content: flex-end;
}

.base-setup-confirm {
  padding: 8px 24px;
  border-radius: 8px;
  border: none;
  background: #3b82f6;
  color: #fff;
  font-size: 0.9em;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}

.base-setup-confirm:hover {
  background: #2563eb;
}
</style>
